import {
  customElement,
  type TemplateResult,
  property,
  state,
  html,
  css,
  cssManager,
  DeesElement,
} from '@design.estate/dees-element';
import * as domtools from '@design.estate/dees-domtools';
import { zIndexRegistry } from '../../00zindex.js';
import { themeDefaultStyles } from '../../00theme.js';
import { DeesWindowLayer } from '../../00group-overlay/dees-windowlayer/dees-windowlayer.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-input-dropdown-popup': DeesInputDropdownPopup;
  }
}

export interface IDropdownSearchOption { key: string; option: string; payload?: unknown; }
export type TDropdownSearch = (query: string, signal: AbortSignal) => Promise<IDropdownSearchOption[]>;

@customElement('dees-input-dropdown-popup')
export class DeesInputDropdownPopup extends DeesElement {
  @property({ type: Array })
  accessor options: { option: string; key: string; payload?: any }[] = [];

  @property({ type: Boolean })
  accessor enableSearch: boolean = true;

  @property({ type: Boolean })
  accessor opensToTop: boolean = false;

  @property({ attribute: false })
  accessor triggerRect: DOMRect | null = null;

  @property({ attribute: false })
  accessor ownerComponent: HTMLElement | null = null;

  @property({ type: String })
  accessor selectedKey = '';

  /** Optional remote search. Results are authoritative and are not filtered again locally. */
  @property({ attribute: false }) accessor searchOptions: TDropdownSearch | undefined;
  @property({ type: String }) accessor searchLabel = 'Search options';
  @state() accessor loading = false;
  @state() accessor searchError = '';
  private searchAbort?: AbortController;

  @state()
  accessor filteredOptions: { option: string; key: string; payload?: any }[] = [];

  @state()
  accessor highlightedIndex: number = 0;

  @state()
  accessor searchValue: string = '';

  @state()
  accessor menuZIndex: number = 1000;

  @state()
  accessor visible: boolean = false;

  private windowLayer: DeesWindowLayer | null = null;
  private isDestroying: boolean = false;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        position: fixed;
        top: 0;
        left: 0;
        width: 0;
        height: 0;
        pointer-events: none;
        font-family: var(--dees-font-family);
        color: var(--dees-color-text-secondary);
      }

      * {
        box-sizing: border-box;
      }

      .selectionBox {
        position: fixed;
        pointer-events: auto;
        display: flex;
        flex-direction: column;
        transition: transform var(--dees-transition-fast) var(--dees-ease-standard), opacity var(--dees-transition-fast) var(--dees-ease-standard);
        opacity: 0;
        transform: translateY(-8px) scale(0.98);
        background: var(--dees-material-regular-bg-opaque);
        border: 1px solid var(--dees-color-border-default);
        box-shadow: var(--dees-shadow-lg);
        min-height: var(--dees-control-height-lg);
        max-height: 300px;
        overflow: hidden;
        border-radius: var(--dees-radius-lg);
        user-select: none;
      }

      @supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
        .selectionBox {
          background: var(--dees-material-regular-bg-opaque);
        }
      }

      .selectionBox.top {
        transform: translateY(8px) scale(0.98);
      }

      .selectionBox.show {
        pointer-events: auto;
        transform: translateY(0) scale(1);
        opacity: 1;
      }

      .options-container {
        min-height: 0;
        max-height: 250px;
        overflow-y: auto;
        padding: var(--dees-spacing-xs);
      }

      .option {
        transition: background-color var(--dees-transition-fast) var(--dees-ease-standard);
        line-height: var(--dees-control-height-sm);
        padding: 0 var(--dees-spacing-sm);
        border-radius: var(--dees-radius-sm);
        margin: 2px 0;
        cursor: pointer;
        font-size: var(--dees-font-control-size);
        color: var(--dees-color-text-secondary);
      }

      .option.highlighted {
        background: var(--dees-color-badge-default-bg);
      }

      .option:hover {
        background: var(--dees-color-badge-default-bg);
        color: var(--dees-color-text-primary);
      }

      .no-options {
        padding: var(--dees-spacing-sm);
        text-align: center;
        font-size: var(--dees-font-control-size);
        color: var(--dees-color-text-muted);
        font-style: italic;
      }

      .search {
        padding: var(--dees-spacing-xs);
        border-bottom: 1px solid var(--dees-color-border-default);
        margin-bottom: var(--dees-spacing-xs);
      }

      .search input {
        display: block;
        width: 100%;
        height: var(--dees-control-height-sm);
        padding: 0 var(--dees-spacing-sm);
        background: transparent;
        border: 1px solid var(--dees-color-border-default);
        border-radius: var(--dees-radius-sm);
        color: inherit;
        font-size: var(--dees-font-control-size);
        font-family: inherit;
        outline: none;
        transition: border-color var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .search input::placeholder {
        color: var(--dees-color-text-muted);
      }

      .search input:focus {
        outline: 2px solid var(--dees-color-focus-ring);
        outline-offset: -2px;
      }
      .options-container:focus-visible { outline: 2px solid var(--dees-color-focus-ring); outline-offset: -2px; }
      @media (prefers-reduced-motion: reduce) { .selectionBox, .option { transition: none; transform: none; } }

      .options-container::-webkit-scrollbar {
        width: 8px;
      }

      .options-container::-webkit-scrollbar-track {
        background: transparent;
      }

      .options-container::-webkit-scrollbar-thumb {
        background: var(--dees-color-border-default);
        border-radius: var(--dees-radius-sm);
      }

      .options-container::-webkit-scrollbar-thumb:hover {
        background: var(--dees-color-border-strong);
      }
    `,
  ];

  public render(): TemplateResult {
    if (!this.triggerRect) return html``;

    const posStyle = this.computePositionStyle();

    return html`
      <div
        class="selectionBox ${this.visible ? 'show' : ''} ${this.opensToTop ? 'top' : 'bottom'}"
        style="${posStyle}; z-index: ${this.menuZIndex};"
        @keydown=${this.handleSearchKeydown}
      >
        ${this.enableSearch
          ? html`
              <div class="search">
                <input
                  type="text"
                  placeholder=${this.searchLabel}
                  role="combobox"
                  aria-label=${this.searchLabel}
                  aria-expanded="true"
                  aria-controls="dropdown-options"
                  aria-activedescendant=${this.filteredOptions.length ? `dropdown-option-${this.highlightedIndex}` : ''}
                  .value="${this.searchValue}"
                  @input="${this.handleSearch}"
                  @click="${(e: Event) => e.stopPropagation()}"
                />
              </div>
            `
          : null}
        <div class="options-container" id="dropdown-options" role="listbox" aria-label=${this.ownerComponent?.getAttribute('label') || 'Options'}
          tabindex=${this.enableSearch ? '-1' : '0'} aria-activedescendant=${this.filteredOptions.length ? `dropdown-option-${this.highlightedIndex}` : ''}>
          ${this.loading || this.searchError || this.filteredOptions.length === 0
            ? html`<div class="no-options" role="status">${this.loading ? 'Searching…' : this.searchError || 'No options found'}</div>`
            : this.filteredOptions.map((option, index) => {
                const isHighlighted = this.highlightedIndex === index;
                return html`
                  <div
                    class="option ${isHighlighted ? 'highlighted' : ''}"
                    id=${`dropdown-option-${index}`} role="option" aria-selected=${option.key === this.selectedKey ? 'true' : 'false'}
                    @click="${() => this.selectOption(option)}"
                    @mouseenter="${() => (this.highlightedIndex = index)}"
                  >
                    ${option.option}
                  </div>
                `;
              })}
        </div>
      </div>
    `;
  }

  private computePositionStyle(): string {
    const rect = this.triggerRect!;
    const width = Math.min(rect.width, window.innerWidth - 24);
    const left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 12));
    const maxHeight = Math.max(0, (this.opensToTop ? rect.top : window.innerHeight - rect.bottom) - 16);

    if (this.opensToTop) {
      const bottom = window.innerHeight - rect.top + 4;
      return `left: ${left}px; width: ${width}px; bottom: ${bottom}px; top: auto; max-height:${maxHeight}px`;
    } else {
      const top = rect.bottom + 4;
      return `left: ${left}px; width: ${width}px; top: ${top}px; max-height:${maxHeight}px`;
    }
  }

  public async show(): Promise<void> {
    this.loading = false;
    this.searchError = '';
    this.filteredOptions = this.options;
    this.highlightedIndex = Math.max(0, this.options.findIndex((option) => option.key === this.selectedKey));
    this.searchValue = '';

    // Create window layer (transparent click-catcher, no dim/blur)
    this.windowLayer = await DeesWindowLayer.createAndShow({ dimmed: false, ownerElement: this.ownerComponent });
    this.windowLayer.addEventListener('click', () => {
      this.dispatchEvent(new CustomEvent('close-request'));
    });

    // Set z-index above the window layer
    this.menuZIndex = zIndexRegistry.getNextZIndex();
    zIndexRegistry.register(this, this.menuZIndex);
    this.style.zIndex = this.menuZIndex.toString();

    this.windowLayer.parentElement!.appendChild(this);

    // Animate in on next frame
    await domtools.plugins.smartdelay.delayFor(0);
    this.visible = true;

    // Add scroll/resize listeners for repositioning
    window.addEventListener('scroll', this.handleScrollOrResize, { capture: true, passive: true });
    window.addEventListener('resize', this.handleScrollOrResize, { passive: true });
    if (this.searchOptions) void this.searchRemote();
  }

  public async hide(): Promise<void> {
    // Guard against double-destruction
    if (this.isDestroying) {
      return;
    }
    this.isDestroying = true;
    this.searchAbort?.abort();

    // Remove scroll/resize listeners
    window.removeEventListener('scroll', this.handleScrollOrResize, { capture: true } as EventListenerOptions);
    window.removeEventListener('resize', this.handleScrollOrResize);

    zIndexRegistry.unregister(this);

    this.searchValue = '';
    this.filteredOptions = this.options;
    this.highlightedIndex = 0;

    // Don't await - let window layer cleanup happen in background for instant visual feedback
    if (this.windowLayer) {
      this.windowLayer.destroy();
      this.windowLayer = null;
    }

    // Animate out via CSS transition
    this.visible = false;
    await domtools.plugins.smartdelay.delayFor(150);

    if (this.parentElement) {
      this.parentElement.removeChild(this);
    }

    this.isDestroying = false;
  }

  public async focusSearchInput(): Promise<void> {
    await this.updateComplete;
    const input = this.shadowRoot!.querySelector<HTMLElement>(this.enableSearch ? '.search input' : '.options-container');
    if (input) input.focus();
  }

  public updateOptions(options: { option: string; key: string; payload?: any }[]): void {
    this.options = options;
    if (this.searchOptions) return;
    // Re-filter with current search value
    if (this.searchValue) {
      const searchLower = this.searchValue.toLowerCase();
      this.filteredOptions = this.options.filter((opt) =>
        opt.option.toLowerCase().includes(searchLower)
      );
    } else {
      this.filteredOptions = this.options;
    }
    this.highlightedIndex = 0;
  }

  private selectOption(option: { option: string; key: string; payload?: any }): void {
    this.dispatchEvent(
      new CustomEvent('option-selected', {
        detail: option,
      })
    );
  }

  private handleSearch = (event: Event): void => {
    const searchTerm = (event.target as HTMLInputElement).value;
    this.searchValue = searchTerm;
    if (this.searchOptions) { void this.searchRemote(); return; }
    const searchLower = searchTerm.toLowerCase();
    this.filteredOptions = this.options.filter((option) =>
      option.option.toLowerCase().includes(searchLower)
    );
    this.highlightedIndex = 0;
  };

  private async searchRemote(): Promise<void> {
    this.searchAbort?.abort();
    const controller = this.searchAbort = new AbortController();
    this.loading = true;
    this.searchError = '';
    this.filteredOptions = [];
    this.highlightedIndex = 0;
    try {
      const options = await this.searchOptions!(this.searchValue, controller.signal);
      if (!controller.signal.aborted) this.filteredOptions = options;
    } catch (error) {
      if (!controller.signal.aborted) this.searchError = error instanceof Error ? error.message : 'Search failed. Try again.';
    } finally {
      if (!controller.signal.aborted) this.loading = false;
    }
  }

  private handleSearchKeydown = (event: KeyboardEvent): void => {
    if (event.isComposing) return;
    const key = event.key;
    const maxIndex = this.filteredOptions.length - 1;

    if (key === 'ArrowDown') {
      event.preventDefault();
      this.highlightedIndex = this.highlightedIndex + 1 > maxIndex ? 0 : this.highlightedIndex + 1;
    } else if (key === 'ArrowUp') {
      event.preventDefault();
      this.highlightedIndex = Math.max(0, this.highlightedIndex - 1 < 0 ? maxIndex : this.highlightedIndex - 1);
    } else if (key === 'Enter') {
      event.preventDefault();
      if (this.filteredOptions[this.highlightedIndex]) {
        this.selectOption(this.filteredOptions[this.highlightedIndex]);
      }
    } else if (key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.dispatchEvent(new CustomEvent('close-request'));
    }
    if (key === 'ArrowUp' || key === 'ArrowDown') {
      void this.updateComplete.then(() => this.shadowRoot?.getElementById(`dropdown-option-${this.highlightedIndex}`)?.scrollIntoView({ block: 'nearest' }));
    }
  };

  private handleScrollOrResize = (): void => {
    this.dispatchEvent(new CustomEvent('reposition-request'));
  };

  async disconnectedCallback() {
    this.searchAbort?.abort();
    window.removeEventListener('scroll', this.handleScrollOrResize, { capture: true } as EventListenerOptions);
    window.removeEventListener('resize', this.handleScrollOrResize);
    zIndexRegistry.unregister(this);
    await super.disconnectedCallback();
  }
}
